import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import clientsData from "../data/private/clients.json";
import locationData from "../data/private/location.json";
import { NavLink } from "react-router-dom";
import { logActivity, completeProgressStep } from "../utils/activity";
import normalizeApiError from "../utils/normalizeApiError";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import CountryCitySelect from "../components/CountryCitySelect";
import useModalBehavior from "../utils/useModalBehavior";
import { createClient, updateClient, deleteClient } from "../api/clients.api";
import { useClients } from "../context/ClientsContext";
import { markSelectionUpdated } from "../utils/debugState";

export default function DashboardClients() {
  const notifySelectionUpdated = () => {
    if (typeof window === "undefined") return;
    markSelectionUpdated("client");
    window.dispatchEvent(new Event("myv_selection_updated"));
  };
  const { clients, refreshClients, upsertClient, removeClient, error: clientsError } =
    useClients();
  const items = clients || [];
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    notes: "",
  });
  const modalRef = useRef(null);
  const handleClose = useCallback(() => {
    setShowModal(false);
    setEditingClient(null);
    setForm({
      name: "",
      phone: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      notes: "",
    });
  }, []);

  const formatValue = useCallback((value) => (value ? value : "—"), []);
  const getClientData = useCallback((client) => client?.data || {}, []);

  const handleSubmit = async () => {
    const storedLocation = (() => {
      try {
        const raw = localStorage.getItem("myv_client_location");
        return raw ? JSON.parse(raw) : {};
      } catch (error) {
        return {};
      }
    })();
    const payload = {
      name: form.name,
      phone: form.phone,
      email: form.email,
      address: form.address || "",
      city: storedLocation.city || form.city || "",
      state: form.state || "",
      pincode: form.pincode || "",
    };

    try {
      let nextItems = items;
      if (editingClient?.id) {
        await updateClient(editingClient.id, payload);
        upsertClient({ id: editingClient.id, data: payload });
        nextItems = items.map((client) =>
          client.id === editingClient.id
            ? { ...client, data: { ...(client.data || {}), ...payload } }
            : client
        );
      } else {
        await createClient(payload);
        const refreshed = await refreshClients();
        nextItems = Array.isArray(refreshed?.items) ? refreshed.items : [];
      }
      const nextActive =
        nextItems.find((client) => getClientData(client)?.name === payload.name) ||
        nextItems[0] ||
        null;
      setActiveClient(nextActive);
      if (getClientData(nextActive)?.name) {
        localStorage.setItem("myv_selected_client", getClientData(nextActive).name);
      }
      if (nextActive?.id) {
        localStorage.setItem("myv_selected_client_id", nextActive.id);
      }
      notifySelectionUpdated();
      setToast(clientsData.toastMessage);
      logActivity(editingClient ? "client_updated" : "client_added", {
        name: payload.name,
      });
      if (!editingClient) {
        completeProgressStep("client_added");
      }
      setForm({
        name: "",
        phone: "",
        email: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
      });
      setEditingClient(null);
      handleClose();
      setLoadError(null);
    } catch (error) {
      logActivity("api_error", { module: "clients" });
      setLoadError(normalizeApiError(error));
      setToast(error?.response?.data?.message || error?.message || "");
    }
  };

  useEffect(() => {
    const shouldOpen = localStorage.getItem("myv_action_add_client");
    if (shouldOpen) {
      setShowModal(true);
      localStorage.removeItem("myv_action_add_client");
    }
  }, []);

  useEffect(() => {
    const storedId = localStorage.getItem("myv_selected_client_id");
    const storedName = localStorage.getItem("myv_selected_client");
    const nextActive =
      items.find((client) => client?.id === storedId) ||
      items.find((client) => getClientData(client)?.name === storedName) ||
      items[0] ||
      null;
    setActiveClient(nextActive);
    if (nextActive?.id) {
      localStorage.setItem("myv_selected_client_id", nextActive.id);
    }
    if (getClientData(nextActive)?.name) {
      localStorage.setItem("myv_selected_client", getClientData(nextActive).name);
    }
    notifySelectionUpdated();
    setLoadError(clientsError ? normalizeApiError(clientsError) : null);
    logActivity("clients_loaded");
  }, [clientsError, items]);

  const handleEdit = (client) => {
    setEditingClient(client);
    const data = getClientData(client);
    setForm({
      name: data?.name || "",
      phone: data?.phone || "",
      email: data?.email || "",
      address: data?.address || "",
      city: data?.city || "",
      state: data?.state || "",
      pincode: data?.pincode || "",
    });
    try {
      localStorage.setItem(
        "myv_client_location",
        JSON.stringify({ country: "", city: getClientData(client)?.city || "" })
      );
    } catch (error) {
      // no-op
    }
    setShowModal(true);
  };

  const handleDelete = async (client) => {
    if (!client?.id) return;
    const confirmed = window.confirm(
      `Delete client ${getClientData(client)?.name || ""}?`
    );
    if (!confirmed) return;
    try {
      await deleteClient(client.id);
      removeClient(client.id);
      if (activeClient?.id === client.id) {
        setActiveClient(null);
      }
      setToast("Client deleted");
    } catch (error) {
      setToast(error?.response?.data?.message || error?.message || "");
      logActivity("api_error", { module: "clients" });
    }
  };

  useEffect(() => {
    const handleAction = (event) => {
      if (event.detail === "add-client") {
        setShowModal(true);
        localStorage.removeItem("myv_action_add_client");
      }
    };
    window.addEventListener("myv_command_action", handleAction);
    return () => window.removeEventListener("myv_command_action", handleAction);
  }, []);

  useModalBehavior(showModal, handleClose, modalRef);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">
          {clientsData.title}
        </h1>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditingClient(null);
            setForm({
              name: "",
              phone: "",
              email: "",
              address: "",
              city: "",
              state: "",
              pincode: "",
              notes: "",
            });
            setShowModal(true);
          }}
        >
          {clientsData.ctaLabel}
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="No clients found"
          message="Create your first client to get started."
          testId="dashboard-empty-clients"
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
          <div className="space-y-3">
            {items.map((client) => (
              <div
                key={client.id}
                role="button"
                tabIndex={0}
                className={`card flex w-full items-start justify-between p-4 text-left transition ${
                  activeClient?.id === client.id
                    ? "border-amber-500 text-slate-900 shadow-md"
                    : "text-slate-600 hover:border-amber-300"
                }`}
                onClick={() => {
                  setActiveClient(client);
                  if (getClientData(client)?.name) {
                    localStorage.setItem(
                      "myv_selected_client",
                      getClientData(client).name
                    );
                  }
                  if (client.id) {
                    localStorage.setItem("myv_selected_client_id", client.id);
                  }
                  notifySelectionUpdated();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setActiveClient(client);
                    if (getClientData(client)?.name) {
                      localStorage.setItem(
                        "myv_selected_client",
                        getClientData(client).name
                      );
                    }
                    if (client.id) {
                      localStorage.setItem("myv_selected_client_id", client.id);
                    }
                    notifySelectionUpdated();
                  }
                }}
              >
                <div className="w-full">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {formatValue(getClientData(client)?.name)}
                  </p>
                      <p className="mt-1 text-xs text-slate-500">
                    {formatValue(getClientData(client)?.phone)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        className="text-amber-600 hover:text-amber-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEdit(client);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-rose-600 hover:text-rose-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDelete(client);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-1 text-xs text-slate-500">
                    <p>ID: {formatValue(client.id)}</p>
                    <p>Email: {formatValue(getClientData(client)?.email)}</p>
                    <p>Phone: {formatValue(getClientData(client)?.phone)}</p>
                    <p>City: {formatValue(getClientData(client)?.city)}</p>
                    <p>State: {formatValue(getClientData(client)?.state)}</p>
                    <p>Address: {formatValue(getClientData(client)?.address)}</p>
                    <p>Pincode: {formatValue(getClientData(client)?.pincode)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {activeClient && (
            <div className="card p-6">
              <p className="text-sm font-semibold text-slate-900">
                {clientsData.detail.title}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                        {formatValue(getClientData(activeClient)?.name)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatValue(getClientData(activeClient)?.email)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatValue(getClientData(activeClient)?.phone)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatValue(getClientData(activeClient)?.address)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                ID: {formatValue(activeClient.id)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                City: {formatValue(getClientData(activeClient)?.city)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                State: {formatValue(getClientData(activeClient)?.state)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Pincode: {formatValue(getClientData(activeClient)?.pincode)}
              </p>
              <NavLink
                className="btn btn-secondary mt-4"
                to="/dashboard/properties"
                onClick={() => {
                  localStorage.setItem(
                    "myv_selected_client",
                    getClientData(activeClient)?.name || ""
                  );
                  notifySelectionUpdated();
                }}
              >
                {clientsData.detail.propertiesLinkLabel}
              </NavLink>
            </div>
          )}
        </div>
      )}

      {loadError && <ErrorState error={loadError} onRetry={refreshClients} />}

      {toast && <div className="card p-4 text-sm text-slate-600">{toast}</div>}

      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          >
            <motion.div
              ref={modalRef}
              className="card modal-panel w-full max-w-md p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="dashboard-client-modal-title"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              onClick={(event) => event.stopPropagation()}
            >
              <h2
                id="dashboard-client-modal-title"
                className="text-lg font-semibold text-slate-900"
              >
                {editingClient ? "Edit Client" : clientsData.modal.title}
              </h2>
              <div className="mt-4 grid gap-3">
                {clientsData.modal.fields.map((field) => (
                  <div key={field.key}>
                    <label className="text-xs text-slate-500">
                      {field.label}
                    </label>
                    <input
                      className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                      placeholder={field.placeholder}
                      value={form[field.key]}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          [field.key]: event.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
                <div>
                  <label className="text-xs text-slate-500">State</label>
                  <input
                    className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Enter state"
                    value={form.state}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, state: event.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Pincode</label>
                  <input
                    className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                    placeholder="Enter pincode"
                    value={form.pincode}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        pincode: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="mt-4">
                <CountryCitySelect
                  storageKey="myv_client_location"
                  labels={locationData.clientModal}
                />
              </div>
              <div className="mt-4">
                <label className="text-xs text-slate-500">Address</label>
                <textarea
                  className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Enter address"
                  rows={3}
                  value={form.address}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      address: event.target.value,
                    }))
                  }
                />
              </div>
              <button
                className="btn btn-primary mt-4 w-full"
                onClick={handleSubmit}
              >
                {editingClient ? "Update Client" : clientsData.modal.submitLabel}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
